<template lang="pug">
.container-fluid.p-3
  .row
    .col-12.col-md-8.col-lg-6.mb-3
      vue-highcharts.rounded.bg-white.shadow-sm.p-1(
        v-if="isChartReady",
        type="chart",
        :options="chartOptions"
      )
    .col-12.col-md-4.col-lg-6.mb-3
      vue-highcharts.rounded.bg-white.shadow-sm.p-1(
        type="chart",
        :options="pieChartOptions"
      )

</template>

<script>
import VueHighcharts from 'vue3-highcharts'

import { getContractsByDateRange } from '@/services/api/contract.api'
import { createContractChartView } from '@/utils/factory/contract/chart-view'
import {
  now,
  shiftByMonthAmount,
  getMonthName,
  toUtcSeconds,
  getDiffInMonth,
  truncateByMonth,
  shiftByDayAmount,
  format
} from '@/utils/date'

export default {
  components: { VueHighcharts },
  data() {
    const pointStart = shiftByMonthAmount(now(), -12)
    const pointEnd = shiftByMonthAmount(now(), 12)
    const colors = [
      '#007bff',
      '#ffc107',
      '#868e96',
      '#28a745',
      '#dc3545',
      '#343a40',
      '#6f42c1',
      '#17a2b8',
      '#f8f9fa'
    ]
    return {
      colors,
      isChartReady: false,
      pointStart,
      pointEnd,
      contracts: [],
      preparedContracts: [],
      colorBySerie: {},
      chartOptions: {
        title: { text: 'Revenue' },
        pointIntervalUnit: 'month',
        plotOptions: {
          series: { pointIntervalUnit: 'month', pointInterval: 1 },
          column: {
            stacking: 'normal'
          }
        },
        credits: { enabled: false },
        tooltip: {
          formatter: function () {
            const month = getMonthName(this.x)
            const serieName = this.series.name
            const currentValue = this.y.toLocaleString() + '₴'
            const currentPercentage = Math.round(this.percentage)
            const totalValue = this.total.toLocaleString() + '₴'
            return `<b>${month}</b><br>${serieName}: <b>${currentValue}</b> / ${currentPercentage}%<br>Σ ${totalValue}`
          }
        },
        series: [],
        chart: { height: 320, type: 'column' },
        xAxis: {
          type: 'datetime',
          plotBands: [
            {
              color: '#FCFFC5',
              from: toUtcSeconds(shiftByDayAmount(now(), -10)),
              to: toUtcSeconds(shiftByDayAmount(now(), 10))
            }
          ]
        },
        yAxis: {
          title: {
            text: ''
          }
        }
      },
      pieChartOptions: {
        chart: {
          plotBackgroundColor: null,
          plotBorderWidth: null,
          plotShadow: false,
          spacingLeft: 20,
          spacingRight: 20,
          height: 320,
          type: 'pie'
        },
        title: {
          text: 'Revenue Share'
        },
        credits: { enabled: false },
        tooltip: {
          pointFormat: `Revenue: <b>{point.y}₴</b>`
        },
        accessibility: {
          point: {
            valueSuffix: '₴'
          }
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              format: '<b>{point.name}</b>: {point.percentage:.1f} %'
            }
          }
        },
        series: [
          {
            name: 'Clients',
            colorByPoint: true,
            data: []
          }
        ]
      }
    }
  },
  async created() {
    this.contracts = await this.prepareContracts()
    this.preparedContracts = this.getValuesByEmail(this.contracts)
    this.setColorByClient()
    this.chartOptions.series = this.prepareChartSeries()
    this.pieChartOptions.series[0].data = this.preparePieChartSeries()
    this.isChartReady = true
  },
  methods: {
    async prepareContracts() {
      const clientToken = this.$cookie.get('clientToken')
      const { data } = await getContractsByDateRange({
        clientToken,
        from: format(this.pointStart),
        to: format(this.pointEnd)
      })
      return data.map((contract) =>
        createContractChartView(contract.attributes)
      )
    },
    getValuesByEmail(contracts) {
      const mergedValues = this.getMergedContracts(contracts)
      const sortedContracts = mergedValues.sort(
        (a, b) => b.contractValue - a.contractValue
      )
      const biggestClients = sortedContracts.slice(0, 4)
      const other = sortedContracts.slice(4)
      let result = [...biggestClients]
      if (other.length) {
        const mergedValue = other[0]
        other.shift()
        other.forEach((contract) => mergedValue.concat(contract))
        mergedValue.setName('Other')
        result.push(mergedValue)
      }
      return result
    },
    getMergedContracts(contracts) {
      const contractsByEmail = contracts
        .filter((contract) => !!contract.contractValue)
        .reduce((acc, cur) => {
          if (!acc[cur.name]) acc[cur.name] = []
          acc[cur.name].push(cur)
          return acc
        }, {})
      const valuesByEmail = {}
      Object.keys(contractsByEmail).forEach((email) => {
        contractsByEmail[email] = contractsByEmail[email].sort((a, b) =>
          a.endDate.localeCompare(b.endDate)
        )
        valuesByEmail[email] = contractsByEmail[email][0]
        contractsByEmail[email].shift()
        contractsByEmail[email].forEach((contract) => {
          valuesByEmail[email].merge(contract)
        })
      })
      return Object.values(valuesByEmail)
    },
    setColorByClient() {
      this.preparedContracts.forEach((contract, index) => {
        this.colorBySerie[contract.name] = this.colors[index]
      })
    },
    prepareChartSeries() {
      const diffInMonth = getDiffInMonth(this.pointStart, this.pointEnd)
      const indexes = [...Array(diffInMonth + 1).keys()]
      const contractValuesComparator = (a, b) =>
        Object.values(a.valuesByDate)[0] - Object.values(b.valuesByDate)[0]
      return this.preparedContracts
        .sort(contractValuesComparator)
        .map((contract) =>
          this.getChartSerie({ contract, monthIndexes: indexes })
        )
    },
    getChartSerie({ contract, monthIndexes }) {
      let data = monthIndexes.map((index) => {
        let date = truncateByMonth(shiftByMonthAmount(this.pointStart, index))
        let formattedDate = format(date)
        return Math.floor(contract.valuesByDate[formattedDate]) || 0
      })
      return {
        name: contract.name,
        data,
        color: this.colorBySerie[contract.name],
        pointStart: toUtcSeconds(this.pointStart)
      }
    },
    preparePieChartSeries() {
      const totalValueComparator = (a, b) => a.contractValue - b.contractValue
      return this.preparedContracts
        .sort(totalValueComparator)
        .map(this.getPieChartSerie)
    },
    getPieChartSerie(contract) {
      return {
        name: contract.name,
        color: this.colorBySerie[contract.name],
        y: contract.contractValue
      }
    }
  }
}
</script>
